/* eslint-disable */
import { useCallback, useEffect, useState } from 'react';
import { Table } from 'antd';

import callApi from '../../Api/config';
import { PAYMENT_VOUCHER } from '../../constants/EntityName';
import { toastError } from '../../service/Toast';
import { formatIsoDate } from '../../utils/functions';
import DefaultTableHeader from '../Entity/AbstractLanding/DefaultTableHeader';
import AbstractTabs, { TabActiosEventType, TabActiosProps } from '../Entity/AbstractTabs';
import PaymentVoucherDetails from './PaymentVoucherDetails';
import AccountingStatusControl from '../../components/PaymentVoucher/AccountingStatusControl';
import {
  accountingStatusValue,
  displayDate,
  money,
  statusTag,
} from '../../components/PaymentVoucher/helpers';

const PaymentVouchers = () => {
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSearch, setFilterSearch] = useState<Record<string, any>>({});
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [activeKey, setActiveKey] = useState('0');
  const [content, setContent] = useState<any[]>([]);
  const [tabVouchers, setTabVouchers] = useState<{ [key: string]: any }>({});

  const statusFilter =
    filterSearch?.status && filterSearch.status !== 'All' ? String(filterSearch.status) : undefined;
  const methodFilter =
    filterSearch?.paymentMethod && filterSearch.paymentMethod !== 'All'
      ? String(filterSearch.paymentMethod)
      : undefined;
  const accountingStatusFilter =
    filterSearch?.accountingStatus && filterSearch.accountingStatus !== 'All'
      ? String(filterSearch.accountingStatus)
      : undefined;

  const fetchVouchers = useCallback(
    async (
      page: number,
      pageSize: number,
      searchValue: string,
      statusValue?: string,
      methodValue?: string,
      accountingStatusValue?: string
    ) => {
      try {
        setLoading(true);
        const res = await callApi(PAYMENT_VOUCHER, 'GET', null, {
          page,
          limit: pageSize,
          search: searchValue?.trim() || undefined,
          status: statusValue || undefined,
          paymentMethod: methodValue || undefined,
          accountingStatus: accountingStatusValue || undefined,
        });
        if (res?.status && res?.data?.data) {
          setVouchers(res.data.data.vouchers || []);
          setTotalCount(res.data.data.totalCount || 0);
        } else {
          toastError(res?.data?.message || 'Failed to fetch payment vouchers');
        }
      } catch (error) {
        toastError('Failed to fetch payment vouchers');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchVouchers(
      pagination.current,
      pagination.pageSize,
      searchQuery,
      statusFilter,
      methodFilter,
      accountingStatusFilter
    );
  }, [
    pagination.current,
    pagination.pageSize,
    searchQuery,
    statusFilter,
    methodFilter,
    accountingStatusFilter,
    fetchVouchers,
  ]);

  const refreshList = useCallback(() => {
    fetchVouchers(
      pagination.current,
      pagination.pageSize,
      searchQuery,
      statusFilter,
      methodFilter,
      accountingStatusFilter
    );
  }, [
    fetchVouchers,
    pagination.current,
    pagination.pageSize,
    searchQuery,
    statusFilter,
    methodFilter,
    accountingStatusFilter,
  ]);

  /** Open tab once — row click only (no cell + row double fire). */
  const openVoucherTab = useCallback((record: any) => {
    const tabKey = String(record?.id);
    if (!tabKey || tabKey === 'undefined') return;

    setActiveKey(tabKey);
    setTabVouchers(prev => (prev[tabKey] ? prev : { ...prev, [tabKey]: record }));
    setContent(prev => {
      if (prev.some(tab => String(tab.key) === tabKey)) {
        return prev;
      }
      return [
        ...prev,
        {
          key: tabKey,
          tabTitle: record.voucherId || record.displayVoucherId || `Voucher ${tabKey}`,
        },
      ];
    });
  }, []);

  const columns: any[] = [
    {
      title: 'Voucher ID',
      dataIndex: 'displayVoucherId',
      render: (value: any, record: any) => (
        <span style={{ textDecoration: 'underline' }}>{value || record.voucherId}</span>
      ),
    },
    {
      title: 'Order ID',
      dataIndex: 'displayOrderId',
      render: (value: any, record: any) => value || record.orderId,
    },
    {
      title: 'Customer Name',
      dataIndex: 'customerDisplayName',
      render: (value: any, record: any) => value || record.customerName || '—',
    },
    {
      title: 'Sender Name',
      dataIndex: 'senderName',
    },
    {
      title: 'Received Date',
      dataIndex: 'receivedDate',
      render: (value: any) => (value ? formatIsoDate(value) : '--'),
    },
    { title: 'Payment Method', dataIndex: 'paymentMethod' },
    // {
    //   title: 'Transaction / Reference ID',
    //   dataIndex: 'transactionId',
    //   render: (value: any) => value || '—',
    // },
    {
      title: 'Received Amount',
      dataIndex: 'receivedAmount',
      render: (value: any, record: any) => money(value, record.currency),
    },
    {
      title: 'Govt Fee',
      dataIndex: 'memberGovtFee',
      render: (value: any, record: any) =>
        money(
          value != null && value !== '' ? value : (record.feeTotals?.['Government Fee'] ?? 0),
          record.currency
        ),
    },
    {
      title: 'Courier Label Fee',
      dataIndex: 'memberCourierFee',
      render: (value: any, record: any) =>
        money(
          value != null && value !== '' ? value : (record.feeTotals?.['Courier Label'] ?? 0),
          record.currency
        ),
    },
    // {
    //   title: 'Withdrawn Amount',
    //   dataIndex: 'withdrawnAmount',
    //   render: (value: any, record: any) => money(value ?? 0, record.currency),
    // },
    {
      title: 'Remaining Amount',
      dataIndex: 'remainingAmount',
      render: (value: any, record: any) => money(value ?? 0, record.currency),
    },
    {
      title: 'Settled Amount',
      dataIndex: 'settledAmount',
      render: (value: any, record: any) => money(value ?? 0, record.currency),
    },
    { title: 'Currency', dataIndex: 'currency' },
    { title: 'Recorded By', dataIndex: 'createdBy', render: (value: any) => value || '—' },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      render: (value: any) => displayDate(value, true),
    },
    { title: 'Voucher Status', dataIndex: 'status', render: statusTag },
    {
      title: 'Accounting Status',
      dataIndex: 'accountingStatus',
      width: 230,
      render: (_: any, record: any) => (
        <AccountingStatusControl value={accountingStatusValue(record)} block />
      ),
    },
    // {
    //   title: '',
    //   key: 'view',
    //   render: () => <span style={{ color: '#1890ff' }}>View</span>,
    // },
  ];

  const listView = (
    <div>
      <DefaultTableHeader
        headerName="Payment Vouchers"
        totalCount={totalCount}
        countText="Vouchers"
        setSearchQuery={(value: any) => {
          setSearchQuery(typeof value === 'string' ? value : String(value || ''));
          setPagination(prev => ({ ...prev, current: 1 }));
        }}
        entityName={PAYMENT_VOUCHER}
        addName="Voucher"
        showFilter={true}
        filterQuery={(filters: any) => {
          setFilterSearch(filters || {});
          setPagination(prev => ({ ...prev, current: 1 }));
        }}
        disableAdd={true}
        showDownload={false}
      />
      <Table
        rowKey={record => record.listKey || `${record.id}-${record.memberId || '0'}`}
        columns={columns}
        dataSource={vouchers}
        loading={loading}
        bordered
        scroll={{ x: 'max-content' }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: totalCount,
          showSizeChanger: true,
          showTotal: (total: number) => `Total ${total} vouchers`,
          onChange: (page: number, pageSize: number) =>
            setPagination({ current: page, pageSize: pageSize || 10 }),
        }}
        onRow={record => ({
          onClick: () => openVoucherTab(record),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );

  return (
    <AbstractTabs
      activeKey={activeKey}
      entityName="Payment Vouchers"
      onTabClick={setActiveKey}
      updateFormVisibility={(action: TabActiosProps, targetKey?: string | TabActiosEventType) => {
        if (action === 'remove') {
          setContent(prev => prev.filter(item => String(item.key) !== String(targetKey)));
          setActiveKey('0');
        }
      }}
      content={content.map(tab => ({
        ...tab,
        content: (
          <PaymentVoucherDetails
            key={tab.key}
            voucher={tabVouchers[tab.key] || {}}
            voucherId={tab.key}
            onVoucherChange={updated => {
              setTabVouchers(curr => ({ ...curr, [tab.key]: updated }));
            }}
            onListRefresh={refreshList}
          />
        ),
      }))}
    >
      {listView}
    </AbstractTabs>
  );
};

export default PaymentVouchers;
